import type { FC } from 'react';
import { lazy } from 'react';
import { Navigate, Route } from 'react-router';
import { useCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { useReferrer } from 'src/hooks/use-referrer';
import { useTrade } from 'src/pages/reporting/my-transactions/trades/allocation-to-balancing-groups/use-trade';
import { ParamsGuard } from 'src/utils/params-guard';

const DetailsPage = lazy(() => import('./details/page'));
const AddPage = lazy(() => import('./add/page'));
const EditPage = lazy(() => import('./edit/page'));

export const TradeAllocationToBalancingGroups: FC = () => {
  const capacityManagementRoot = useCapacityManagementRoot();
  const trade = useTrade({ capacityManagementRoot });
  const referrer = useReferrer({
    label: 'My Transactions',
    location: '/reporting/my-transactions',
  });

  // avoid pop in of page titles in case of 401s
  if (!capacityManagementRoot.response) return <PageSpinner />;

  return (
    <RootRoutes>
      <Route
        path="/reporting/my-transactions/trades/:id/allocation-to-balancing-groups/:direction/:balancingGroupAllocationId/edit"
        element={
          <ParamsGuard name="direction" regExp={/^(exit|entry)$/}>
            <EditPage
              trade={trade}
              capacityManagementRootResponse={
                capacityManagementRoot.response.data
              }
            />
          </ParamsGuard>
        }
      />

      <Route
        path="/reporting/my-transactions/trades/:id/allocation-to-balancing-groups/:direction/add"
        element={
          <ParamsGuard name="direction" regExp={/^(exit|entry)$/}>
            <AddPage
              trade={trade}
              capacityManagementRootResponse={
                capacityManagementRoot.response.data
              }
            />
          </ParamsGuard>
        }
      />

      <Route
        path="/reporting/my-transactions/trades/:id/allocation-to-balancing-groups/:direction"
        element={
          <ParamsGuard name="direction" regExp={/^(exit|entry)$/}>
            <DetailsPage
              trade={trade}
              capacityManagementRootResponse={
                capacityManagementRoot.response.data
              }
            />
          </ParamsGuard>
        }
      />

      <Route
        path="/reporting/my-transactions/trades/:id/allocation-to-balancing-groups"
        element={
          !trade.response ? (
            <PageSpinner />
          ) : trade.response.data.exit &&
            trade.response.data.exit._links?.balancingGroupAllocations?.href ? (
            <Navigate
              replace
              {...referrer.getLinkProps({
                pathname: `/reporting/my-transactions/trades/${trade.response.data.id}/allocation-to-balancing-groups/exit`,
              })}
            />
          ) : (
            <Navigate
              replace
              {...referrer.getLinkProps({
                pathname: `/reporting/my-transactions/trades/${trade.response.data.id}/allocation-to-balancing-groups/entry`,
              })}
            />
          )
        }
      />

      <Route path="*" element={<ThrowNotFound />} />
    </RootRoutes>
  );
};
